import React from "react";
import { useDocumentMeta, useEditorUi, useViewport } from "../store/selectors";
import { type GridAppearance, gridAppearance } from "./gridScale";

export interface GridLayerProps {
  patternId: string;
  /** Which copy this is: the one under the document draws the page too;
   *  the one over it draws only the grid, and only when zoomed in. */
  placement: "under" | "over";
}

/** Editing area (the white page) and the grid. The grid follows the zoom
 *  level (see `gridAppearance`) and moves above the shapes when zoomed in
 *  far enough to place things against it. */
export const GridLayer: React.FC<GridLayerProps> = ({
  patternId,
  placement
}) => {
  const { editingZoneWidth, editingZoneHeight, gridSize } = useDocumentMeta();
  const { showGrid } = useEditorUi();
  const { scale } = useViewport();
  const appearance = showGrid ? gridAppearance(gridSize, scale) : null;
  const isOver = placement === "over";
  const grid =
    appearance && appearance.isOverShapes === isOver ? appearance : null;
  if (isOver) {
    return grid ? (
      <g data-layer="grid-over">
        <defs>
          <GridPattern patternId={patternId} step={grid.step} />
        </defs>
        <GridRect
          patternId={patternId}
          grid={grid}
          width={editingZoneWidth}
          height={editingZoneHeight}
        />
      </g>
    ) : null;
  }
  return (
    <g data-layer="editing-area">
      <defs>
        {grid ? <GridPattern patternId={patternId} step={grid.step} /> : null}
        <filter
          id={`${patternId}-shadow`}
          x="-5%"
          y="-5%"
          width="110%"
          height="110%"
        >
          <feDropShadow dx="0" dy="0" stdDeviation="10" floodOpacity="0.15" />
        </filter>
      </defs>
      <rect
        width={editingZoneWidth}
        height={editingZoneHeight}
        fill="white"
        filter={`url(#${patternId}-shadow)`}
      />
      {grid ? (
        <GridRect
          patternId={patternId}
          grid={grid}
          width={editingZoneWidth}
          height={editingZoneHeight}
        />
      ) : null}
    </g>
  );
};

interface GridPatternProps {
  patternId: string;
  step: number;
}

const GridPattern: React.FC<GridPatternProps> = ({ patternId, step }) => (
  <pattern
    id={patternId}
    data-grid-pattern
    width={step}
    height={step}
    patternUnits="userSpaceOnUse"
  >
    <path
      d={`M ${step} 0 L 0 0 0 ${step}`}
      fill="none"
      stroke="rgba(0, 0, 0, 0.08)"
      strokeWidth={1}
      vectorEffect="non-scaling-stroke"
    />
  </pattern>
);

interface GridRectProps {
  patternId: string;
  grid: GridAppearance;
  width: number;
  height: number;
}

const GridRect: React.FC<GridRectProps> = ({
  patternId,
  grid,
  width,
  height
}) => (
  <rect
    data-grid-step={grid.step}
    width={width}
    height={height}
    fill={`url(#${patternId})`}
    opacity={grid.opacity}
    pointerEvents="none"
  />
);
